/**
 * Theme (light/dark) infrastructure.
 *
 * Everything here is pure so the resolution rules can be tested without a DOM.
 * The React binding lives in src/saas/contexts/ThemeContext.tsx.
 *
 * Tailwind runs in `darkMode: 'class'`, so a single `dark` class on the html
 * element is the only switch: "system" is resolved to a concrete theme here
 * rather than being delegated to the `prefers-color-scheme` media variant.
 * That keeps the applied theme observable and lets an explicit choice win.
 */

import {
  applyNightPaletteToElement,
  clearNightPaletteFromElement,
  type NightPaletteTokens,
} from './nightPalette';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'] as const;

export const THEME_MODE_LABELS: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Match device',
};

/** Per-user preference. Scoped by user so a shared device keeps choices apart. */
export function themeStorageKey(userId?: string | null): string {
  return userId ? `saas_theme_mode:${userId}` : 'saas_theme_mode';
}

export function sanitizeThemeMode(value: unknown, fallback: ThemeMode = 'light'): ThemeMode {
  return THEME_MODES.includes(value as ThemeMode) ? (value as ThemeMode) : fallback;
}

export function resolveTheme(mode: ThemeMode, prefersDark: boolean): ResolvedTheme {
  if (mode === 'system') return prefersDark ? 'dark' : 'light';
  return mode;
}

/**
 * The theme actually applied, taking the platform switch into account. When
 * dark mode is turned off platform-wide, everyone renders light regardless of
 * their stored preference — the preference is kept, not erased.
 */
export function effectiveTheme(
  mode: ThemeMode,
  prefersDark: boolean,
  darkModeAllowed: boolean,
): ResolvedTheme {
  if (!darkModeAllowed) return 'light';
  return resolveTheme(mode, prefersDark);
}

/** Super Admin: how the public homepage and marketing pages open. */
export const MARKETING_THEME_KEY = 'saas_marketing_theme';

export type MarketingThemeMode = 'light' | 'dark' | 'system' | 'follow_workspace';

export const MARKETING_THEME_MODES: readonly MarketingThemeMode[] = [
  'light',
  'dark',
  'system',
  'follow_workspace',
] as const;

export const MARKETING_THEME_LABELS: Record<MarketingThemeMode, string> = {
  light: 'Day (light)',
  dark: 'Night (dark)',
  system: 'Match device',
  follow_workspace: 'Follow workspace theme',
};

export function sanitizeMarketingThemeMode(
  value: unknown,
  fallback: MarketingThemeMode = 'light',
): MarketingThemeMode {
  return MARKETING_THEME_MODES.includes(value as MarketingThemeMode)
    ? (value as MarketingThemeMode)
    : fallback;
}

/**
 * toolsandbubbles.com is always day (light), including when the device is in
 * night mode. ServiceApp marketing/workspace keep Super Admin theme controls.
 */
export function shouldForceDayTheme(host: string, appMode?: string): boolean {
  const h = (host || '').toLowerCase();
  if (h === 'serviceapp.ai' || h.endsWith('.serviceapp.ai') || h.startsWith('serviceapp-platform')) {
    return false;
  }
  if (h === 'toolsandbubbles.com' || h.endsWith('.toolsandbubbles.com')) return true;
  return appMode === 'tb-site';
}

/**
 * Theme applied to the document. Marketing content (homepage, blog, SEO)
 * defaults to day mode so a stored login/workspace night preference cannot
 * paint the public site dark on first load.
 */
export function effectiveDocumentTheme(
  mode: ThemeMode,
  prefersDark: boolean,
  darkModeAllowed: boolean,
  options?: {
    isMarketingContent?: boolean;
    marketingTheme?: MarketingThemeMode;
    forceDay?: boolean;
  },
): ResolvedTheme {
  if (options?.forceDay) return 'light';
  const marketingTheme = options?.marketingTheme ?? 'light';
  if (options?.isMarketingContent && marketingTheme !== 'follow_workspace') {
    if (!darkModeAllowed) return 'light';
    if (marketingTheme === 'dark') return 'dark';
    if (marketingTheme === 'system') return resolveTheme('system', prefersDark);
    return 'light';
  }
  return effectiveTheme(mode, prefersDark, darkModeAllowed);
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function readStoredThemeMode(userId?: string | null): ThemeMode | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(themeStorageKey(userId));
    return raw ? sanitizeThemeMode(raw) : null;
  } catch {
    return null;
  }
}

export function storeThemeMode(mode: ThemeMode, userId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(themeStorageKey(userId), mode);
  } catch {
    // Private browsing or a full quota: the theme still applies for this session.
  }
}

type ClassListLike = { add: (token: string) => void; remove: (token: string) => void };
type DocumentElementLike = {
  classList: ClassListLike;
  style: {
    colorScheme?: string;
    setProperty?: (name: string, value: string) => void;
    removeProperty?: (name: string) => void;
  };
};

/**
 * Toggle the `dark` class and native color-scheme. Setting color-scheme is what
 * makes scrollbars, date pickers and other UA widgets follow the theme.
 */
export function applyThemeToDocument(
  element: DocumentElementLike | null | undefined,
  theme: ResolvedTheme,
  nightTokens?: NightPaletteTokens,
): void {
  if (!element) return;

  if (theme === 'dark') {
    element.classList.add('dark');
    if (nightTokens) applyNightPaletteToElement(element.style, nightTokens);
  } else {
    element.classList.remove('dark');
    clearNightPaletteFromElement(element.style);
  }

  element.style.colorScheme = theme;
}

/** Pin the document to day mode so the UA cannot auto-darken the page. */
export function applyDayThemeLock(element: DocumentElementLike | null | undefined): void {
  applyThemeToDocument(element, 'light');
  if (!element) return;
  if (typeof element.style.setProperty === 'function') {
    element.style.setProperty('color-scheme', 'light only');
  } else {
    element.style.colorScheme = 'light';
  }
}

/** Next mode when cycling through the switcher, e.g. from a keyboard shortcut. */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  const index = THEME_MODES.indexOf(mode);
  return THEME_MODES[(index + 1) % THEME_MODES.length];
}

/**
 * What the sun/moon control should apply. Users expect one tap to flip the
 * colours they see — not to land on “match device”, which can look unchanged.
 */
export function oppositeResolvedTheme(theme: ResolvedTheme): ThemeMode {
  return theme === 'dark' ? 'light' : 'dark';
}
